import { getApiBaseUrl } from '../config/api';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  role: 'PATIENT' | 'CAREGIVER' | 'ADMIN';
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  success: boolean;
  data: {
    user: User;
    token: string;
  };
  message: string;
  timestamp: string;
}

export interface CheckEmailResponse {
  success: boolean;
  data: {
    email: string;
    exists: boolean;
  };
  timestamp: string;
}

export interface ApiError {
  success: false;
  error: string;
  message: string;
  timestamp: string;
}

export interface AdminStats {
  totalUsers: number;
  totalPosts: number;
  totalComments: number;
  activeUsers: number;
}

class AuthService {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const baseUrl = await getApiBaseUrl();
    const url = `${baseUrl}/auth${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      // #region agent log
      fetch('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c313e2'},body:JSON.stringify({sessionId:'c313e2',runId:'pre-fix',hypothesisId:'A',location:'admin-web/src/services/authService.ts:makeRequest',message:'admin auth request starting',data:{baseUrl,endpoint,urlHost:(()=>{try{return new URL(url).host}catch{return 'invalid'}})()},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        const code = typeof data.error === 'string' ? data.error : '';
        const message = data.message || `HTTP error! status: ${response.status}`;
        throw new Error(code ? `${code}: ${message}` : message);
      }

      return data;
    } catch (error) {
      // #region agent log
      fetch('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c313e2'},body:JSON.stringify({sessionId:'c313e2',runId:'pre-fix',hypothesisId:'A',location:'admin-web/src/services/authService.ts:makeRequest:catch',message:'admin auth request failed',data:{baseUrl,endpoint,errorName:error instanceof Error ? error.name : 'unknown',errorMessage:error instanceof Error ? error.message : String(error),isNameNotResolved:error instanceof Error && /ERR_NAME_NOT_RESOLVED|Failed to fetch|NetworkError/i.test(error.message)},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('An unexpected error occurred');
    }
  }

  async checkEmail(email: string): Promise<CheckEmailResponse> {
    return this.makeRequest<CheckEmailResponse>('/check-email', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    return this.makeRequest<AuthResponse>('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async register(email: string, password: string, firstName: string, lastName: string): Promise<AuthResponse> {
    return this.makeRequest<AuthResponse>('/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, firstName, lastName, confirmPassword: password }),
    });
  }

  async getProfile(): Promise<{ success: boolean; data: User }> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    return this.makeRequest<{ success: boolean; data: User }>('/profile', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async updateProfile(updates: { firstName?: string; lastName?: string; avatar?: string }): Promise<{ success: boolean; data: User }> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    return this.makeRequest<{ success: boolean; data: User }>('/profile', {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updates),
    });
  }

  async deleteAccount(): Promise<{ success: boolean; message: string }> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    return this.makeRequest<{ success: boolean; message: string }>('/profile', {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // Admin-specific methods
  async getAdminStats(): Promise<{ success: boolean; data: AdminStats }> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    return this.makeRequest<{ success: boolean; data: AdminStats }>('/admin/stats', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getAllUsers(): Promise<{ success: boolean; data: User[] }> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    return this.makeRequest<{ success: boolean; data: User[] }>('/admin/users', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // Token management
  setToken(token: string): void {
    localStorage.setItem('authToken', token);
  }

  getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  removeToken(): void {
    localStorage.removeItem('authToken');
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // Role validation helpers
  isAdmin(user: User | null): boolean {
    return user?.role === 'ADMIN';
  }

  isCaregiver(user: User | null): boolean {
    return user?.role === 'CAREGIVER';
  }

  isPatient(user: User | null): boolean {
    return user?.role === 'PATIENT';
  }

  canAccessAdminPanel(user: User | null): boolean {
    return this.isAdmin(user);
  }

  canAccessCaregiverFeatures(user: User | null): boolean {
    return this.isCaregiver(user) || this.isAdmin(user);
  }

  canAccessPatientFeatures(user: User | null): boolean {
    return this.isPatient(user) || this.isCaregiver(user) || this.isAdmin(user);
  }

  // Check if user has admin access before allowing login to admin app
  async validateAdminAccess(): Promise<boolean> {
    try {
      const profile = await this.getProfile();
      return this.canAccessAdminPanel(profile.data);
    } catch (error) {
      console.error('Admin access validation failed:', error);
      return false;
    }
  }

  /** FEAT-076 — Verify MFA claim is present on the session token (MVP-18.28). */
  async validateAdminMfaAccess(): Promise<boolean> {
    try {
      await this.getAdminStats();
      // #region agent log
      fetch('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c313e2'},body:JSON.stringify({sessionId:'c313e2',runId:'post-fix',hypothesisId:'M2',location:'admin-web/src/services/authService.ts:validateAdminMfaAccess',message:'MFA probe passed',data:{ok:true},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const mfaRequired = message.includes('ADMIN_MFA_REQUIRED');
      // #region agent log
      fetch('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c313e2'},body:JSON.stringify({sessionId:'c313e2',runId:'post-fix',hypothesisId:'M2',location:'admin-web/src/services/authService.ts:validateAdminMfaAccess',message:'MFA probe failed',data:{mfaRequired,errorMessage:message.slice(0,200)},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      if (mfaRequired) {
        return false;
      }
      // Non-MFA failures (network, etc.) — allow login; route guards will surface errors.
      return true;
    }
  }

  // Admin-specific user management
  async updateUserRole(userId: string, role: 'PATIENT' | 'CAREGIVER' | 'ADMIN'): Promise<{ success: boolean; data: User }> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const baseUrl = await getApiBaseUrl();
    const url = `${baseUrl}/users/${userId}/role`;
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ role }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  }

  async getAdminUsers(): Promise<{ success: boolean; data: User[] }> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const baseUrl = await getApiBaseUrl();
    const url = `${baseUrl}/users/admins`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  }

  async getUsersByRole(role: 'PATIENT' | 'CAREGIVER' | 'ADMIN'): Promise<{ success: boolean; data: User[] }> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const baseUrl = await getApiBaseUrl();
    const url = `${baseUrl}/users/by-role/${role}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  }

  async getCaregiverUsers(): Promise<{ success: boolean; data: User[] }> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const baseUrl = await getApiBaseUrl();
    const url = `${baseUrl}/users/caregivers`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  }

  async getPatientUsers(): Promise<{ success: boolean; data: User[] }> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const baseUrl = await getApiBaseUrl();
    const url = `${baseUrl}/users/patients`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  }
}

export const authService = new AuthService();
